import { Controller } from "@hotwired/stimulus"

// Upgrades a confirmation disclosure into a modal confirmation.
//
// What it enhances already works. `ConfirmActionComponent` renders a
// `<details>`: a summary, and behind it the sentence and the real submit
// button. Confirming is two deliberate acts with the cost stated in between,
// in the HTML, before this file runs. Nothing here rescues a dead button,
// which is why nothing here has to.
//
// Three jobs.
//
// **The summary stops being a disclosure.** A click on it is prevented, so the
// details never opens, and `requestSubmit(fallbackTarget)` is called instead.
// That is not a detour: `requestSubmit` runs the form's constraint validation
// exactly as a click on the fallback button would, so a `required` field left
// empty is reported where it is and the modal never appears. Opening the dialog
// from the click directly would put a confirmation in front of a form that is
// going to be refused, and the person would confirm a change that never
// happens.
//
// **The submit is intercepted on the form, not on a button.** The browser fires
// `submit` only once validation has passed, `event.submitter` says which button
// asked, and Turbo's own listener sits on the document and checks
// `defaultPrevented`, so preventing the default on the form stops Turbo
// cleanly. The review queue's form carries Save and Publish and only Publish
// asks; a form with two of these gets two listeners, each minding its own
// buttons. Nothing here fetches or navigates by hand.
//
// **The summary says what it has become.** A disclosure that opens a modal is a
// button, and it is announced as one from `connect` onwards. Written here
// rather than in the template because it is only true here: a page whose bundle
// failed would otherwise announce a popup that no click can open, over a
// disclosure that is working perfectly.
//
// The listener is added to `element.closest("form")` rather than being wired
// from the markup, so a caller writes `form_with` and nothing else.
export default class extends Controller {
  static targets = ["trigger", "disclosure", "fallback", "dialog", "confirm", "sentinel"]

  connect() {
    this.form = this.element.closest("form")
    if (!this.form) return

    // Safari shipped requestSubmit in 16. Without it there is no way to run the
    // form's validation before showing the dialog, and a confirmation shown
    // over a form that will be refused is worse than the disclosure. So the
    // upgrade simply does not happen, and the disclosure stays a disclosure.
    this.enhanced = typeof this.form.requestSubmit === "function"
    if (!this.enhanced) return

    this.request = this.request.bind(this)
    this.returnFocus = this.returnFocus.bind(this)

    this.form.addEventListener("submit", this.request)
    this.dialogTarget.addEventListener("close", this.returnFocus)

    this.triggerTarget.setAttribute("role", "button")
    this.triggerTarget.setAttribute("aria-haspopup", "dialog")
  }

  disconnect() {
    if (!this.form || !this.enhanced) return

    this.form.removeEventListener("submit", this.request)
    if (this.hasDialogTarget) this.dialogTarget.removeEventListener("close", this.returnFocus)

    // The summary goes back to announcing itself as the disclosure it is again
    // the moment this stops running. Leaving `role="button"` and
    // `aria-haspopup="dialog"` behind is the same lie `connect` avoids telling,
    // pointing the other way: a popup that nothing can open, over a control
    // that is working. Turbo's cached snapshot is where this shows up, since it
    // is taken with the controllers disconnected and restored on the Back
    // button before they connect again.
    if (this.hasTriggerTarget) {
      this.triggerTarget.removeAttribute("role")
      this.triggerTarget.removeAttribute("aria-haspopup")
    }
  }

  open(event) {
    // Not enhanced: leave the click alone and let the disclosure open, which is
    // the whole fallback and needs nothing from this file.
    if (!this.enhanced) return

    // Preventing the default on a summary's click is what stops the `<details>`
    // toggling. The disclosure's contents stay hidden for as long as this
    // controller is running.
    event.preventDefault()
    // Validation first, dialog second. This fires the form's `submit` event
    // with the fallback button as the submitter, which `request` below catches.
    this.form.requestSubmit(this.fallbackTarget)
  }

  request(event) {
    // The confirmed submission, on its way out. Close first: Turbo caches the
    // page as it stands when you leave it, and a snapshot holding an open
    // <dialog> comes back on the Back button as a panel wedged into the middle
    // of the list. `close()` queues its event rather than firing it inline, so
    // the submission this is standing in the middle of is untouched.
    if (event.submitter === this.confirmTarget) {
      this.dialogTarget.close()
      return
    }

    // The sentinel is what Enter reaches: it is the form's default button, and
    // implicit submission clicks the default button rather than anything the
    // reader can see. With no bundle the server refuses whatever it sends. Here
    // there is a better answer, so Enter opens the confirmation instead of
    // making a round trip to be told no. Validation has already run: the submit
    // event does not fire at all while a required field is empty, so there is
    // nothing left to check and `requestSubmit` would be a no-op inside this
    // handler anyway.
    //
    // Anything else in the form, Save included, submits as it always did.
    if (event.submitter !== this.fallbackTarget &&
        !(this.hasSentinelTarget && event.submitter === this.sentinelTarget)) return

    event.preventDefault()
    // showModal(), never the `open` attribute: the top layer, the inert page
    // behind it, the focus trap and Escape are all the browser's, and every
    // hand-rolled version of that list gets the focus handling wrong.
    this.dialogTarget.showModal()
  }

  // Escape, the close button, the backdrop and Cancel all end in the dialog's
  // `close` event, so focus comes back to the summary from all four. Browsers
  // mostly restore it themselves, but not when the trigger never held focus in
  // the first place, which is what a mouse click on it does on macOS.
  returnFocus() {
    if (this.hasTriggerTarget) this.triggerTarget.focus()
  }
}
